import { Info } from 'lucide-react';

import { useSetDisposition } from '@/api/queries';
import type { Disposition, IdeaDetail } from '@/api/types';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { Modal } from '@/ui/Modal';

import { RefineDialog } from '../RefineDialog';
import { SubmissionDialog } from '../SubmitIdeaDialog';
import { HistoryTable } from './HistoryTable';
import type { IdeaDetailUi } from './useIdeaDetail';

const DISPOSITION_COPY: Record<Disposition, { title: string; verb: string; body: string }> = {
  parked: { title: 'Park this idea?', verb: 'Park idea', body: 'It leaves the active matrix and waits on the Parked tab. Nothing is deleted.' },
  archived: { title: 'Archive this idea?', verb: 'Archive idea', body: 'It moves to the Archived tab. Its research and history are kept.' },
  active: { title: 'Reactivate this idea?', verb: 'Reactivate', body: 'It returns to the active matrix.' },
};

function HistoryDialog({ idea, ui }: { idea: IdeaDetail; ui: IdeaDetailUi }) {
  const close = () => ui.setPanel(null);
  const view = (id: string | null) => {
    ui.setPanel(null);
    ui.setViewing(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  return (
    <Modal
      title="Research history"
      subtitle={`${idea.history.length} ${idea.history.length === 1 ? 'run' : 'runs'}, newest first`}
      size="xwide"
      onClose={close}
      footer={<button type="button" className="btn btn-secondary" onClick={close}>Close</button>}
    >
      <p className="idea-ai-caveat">
        <Info aria-hidden="true" />
        Every run is written by AI from the model's own knowledge, without web search. Market figures are estimates; check them before
        using them with a client.
      </p>
      <HistoryTable idea={idea} viewing={ui.viewing} onView={view} />
    </Modal>
  );
}

function DispositionDialog({ idea, to, onDone }: { idea: IdeaDetail; to: Disposition; onDone: () => void }) {
  const setDisposition = useSetDisposition();
  const copy = DISPOSITION_COPY[to];
  return (
    <ConfirmDialog
      title={copy.title}
      confirmLabel={copy.verb}
      pending={setDisposition.isPending}
      error={setDisposition.error}
      onClose={() => {
        setDisposition.reset();
        onDone();
      }}
      onConfirm={() => setDisposition.mutate({ id: idea.id, disposition: to }, { onSuccess: onDone })}
    >
      <p>{copy.body}</p>
    </ConfirmDialog>
  );
}

/** The page's dialogs: what was submitted, the research history, Refine, and park, archive or reactivate. */
export function IdeaDialogs({ idea, ui }: { idea: IdeaDetail; ui: IdeaDetailUi }) {
  const { panel, confirm } = ui;
  const closeConfirm = () => ui.setConfirm(null);
  return (
    <>
      {panel === 'submission' && <SubmissionDialog idea={idea} onClose={() => ui.setPanel(null)} />}
      {panel === 'history' && <HistoryDialog idea={idea} ui={ui} />}
      {confirm?.kind === 'rerun' && (
        <RefineDialog
          idea={idea}
          onClose={closeConfirm}
          onQueued={() => {
            ui.setConfirm(null);
            ui.setViewing(null);
          }}
        />
      )}
      {confirm?.kind === 'disposition' && <DispositionDialog idea={idea} to={confirm.to} onDone={closeConfirm} />}
    </>
  );
}
